import React from "react";
import { Theme, ThemeFontVariant } from "../types";
import { BemHelper } from "@/utils/BemHelper";
import classNames from "classnames";
import CtImage from "../image/CtImage";
import CtCardFeature, { CtCardFeatureProps } from "./CtCardFeature";
import { THEMES, THEME_FONT_VARIANTS } from "../constants";

export type CtCardProps = {
  title: string;
  imageSrc?: string;
  description?: string;
  orientation?: "horizontal" | "vertical";
  size?: "small" | "medium" | "large";
  theme?: Theme;
  fontVariant?: ThemeFontVariant;
  className?: string;
  children?: React.ReactNode;
  features: CtCardFeatureProps[];
  onClick?: () => void;
};

const bem = new BemHelper("ct-card");

function CtCard({
  title,
  imageSrc,
  description,
  orientation,
  size,
  theme,
  fontVariant,
  className,
  children,
  features,
  onClick,
}: CtCardProps) {
  const hasFeatures = features.length > 0;

  return (
    <div
      className={classNames(
        bem.block(),
        bem.modifier(orientation || "horizontal"),
        bem.modifier(size || "medium"),
        bem.modifier(theme || THEMES.THEME_1),
        bem.modifier(fontVariant || THEME_FONT_VARIANTS.FONT_VARIANT_1),
        onClick && bem.modifier("clickable"),
        className,
      )}
      onClick={onClick}
    >
      <CtImage
        src={imageSrc}
        theme={theme}
        objectFit='cover'
        square
        className={bem.element("image")}
      />
      <div>
        <h4 className={classNames(bem.element("title"))}>{title}</h4>
        {hasFeatures && (
          <div className={classNames(bem.element("features-container"))}>
            {features.map((feature, index) => (
              <CtCardFeature key={index} {...feature} />
            ))}
          </div>
        )}
      </div>
      {description && <p className={classNames(bem.element("description"))}>{description}</p>}
      {children && <div className={classNames(bem.element("content"))}>{children}</div>}
    </div>
  );
}

CtCard.defaultProps = {
  orientation: "horizontal",
  imageSrc: undefined,
  size: "medium",
  theme: THEMES.THEME_1,
  fontVariant: THEME_FONT_VARIANTS.FONT_VARIANT_1,
  children: undefined,
  description: undefined,
  features: [],
  onClick: undefined,
};

export default CtCard;
